/**
 * useObstacleDragInteraction — Drag, select, and keyboard interactions for obstacles
 *
 * In Layout Designer (edit mode):
 * - Click obstacle → select (raycast → instanceId → obstacleId)
 * - Drag → move (screen→world, update position)
 * - R → rotate 90°, Shift+R → rotate 15°
 * - Delete → soft-delete
 * - Ctrl+D → duplicate at +2ft offset
 * - Click-to-place from preset
 *
 * In Ramp View (dynamic obstacles only):
 * - Click dynamic obstacle → show popover (handled by parent component)
 */

import { useEffect, useRef, useCallback } from 'react'
import * as THREE from 'three'
import { useQueryClient } from '@tanstack/react-query'
import { useObstacleStore } from '@/stores/obstacleStore'
import { useObstacleMutations, obstacleKeys } from '@/hooks/useObstacles'
import { screenToWorldPosition } from '@/utils/coordinates'
import { METERS_TO_FEET, FEET_TO_METERS } from '@/constants/units'
import { detectHangarForPosition } from '@/lib/utils/hangar-detection'
import { getObstacleScenePosition } from '@/lib/utils/obstacle-coordinates'
import type { Obstacle, CreateObstacleInput } from '@/types/obstacle'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

interface UseObstacleDragInteractionParams {
  map: mapboxgl.Map | null
  sceneObjects: {
    scene: THREE.Scene | null
    camera: THREE.Camera | null
    groundPlane: THREE.Mesh | null
  } | null
  enabled: boolean
  /** All obstacles in the current location */
  obstacles: Obstacle[]
  /** Location ID for creating obstacles */
  locationId: string
  /** All buildings at this location (for unit detection) */
  buildings: Building3DConfigExtended[]
  /** Currently editing building ID */
  editingBuildingId: string | null
  /** Fixed scene origin (set at map load) — do NOT use map.getCenter() */
  mapCenter: { lat: number; lng: number } | null
  /** Callback after obstacle position changes */
  onObstacleUpdate?: () => void
}

const DRAG_THRESHOLD_PIXELS = 5
export function useObstacleDragInteraction({
  map,
  sceneObjects,
  enabled,
  obstacles,
  locationId,
  buildings,
  editingBuildingId,
  mapCenter,
  onObstacleUpdate,
}: UseObstacleDragInteractionParams) {
  const {
    selectedObstacleId, selectObstacle,
    isPlacingMode, placingPreset, cancelPlacing,
    isCreatingMode, creatingForBuildingId, creatingDefaults, cancelCreating,
    setDragging, setPreviewPosition,
  } = useObstacleStore()
  const { create, update, remove, duplicate } = useObstacleMutations()
  const queryClient = useQueryClient()

  // Push position/rotation to TanStack Query cache (no DB round-trip)
  const pushToCache = useCallback((id: string, patch: Partial<Obstacle>) => {
    const queries = queryClient.getQueriesData<Obstacle[]>({ queryKey: obstacleKeys.lists() })
    for (const [key] of queries) {
      queryClient.setQueryData<Obstacle[]>(key, (old) =>
        old?.map((o) => (o.id === id ? { ...o, ...patch } : o))
      )
    }
  }, [queryClient])

  // Refs for drag state
  const isDragging = useRef(false)
  const dragStartScreen = useRef<[number, number]>([0, 0])
  const dragStartWorld = useRef<[number, number]>([0, 0])
  const dragStartObstaclePos = useRef<[number, number]>([0, 0])
  const dragObstacleId = useRef<string | null>(null)
  const dragBuildingRotDeg = useRef<number>(0)

  // Refs for rotation ring drag
  const isRotatingRing = useRef(false)
  const ringDragStartAngle = useRef<number>(0)
  const ringDragStartRotation = useRef<number>(0)
  const ringObstacleId = useRef<string | null>(null)

  // AABB/circle hit testing against obstacles in the editing building
  // Uses screen→world conversion (proven pattern from building/aircraft drag)
  // instead of Three.js raycasting (broken due to combined Mapbox camera matrices)
  const hitTestObstacles = useCallback(
    (screenX: number, screenY: number): string | null => {
      if (!map || !sceneObjects?.scene || !mapCenter) return null

      const canvas = map.getCanvas()
      const rect = canvas.getBoundingClientRect()
      const ndc = new THREE.Vector2(
        ((screenX - rect.left) / rect.width) * 2 - 1,
        -((screenY - rect.top) / rect.height) * 2 + 1
      )

      const worldPos = screenToWorldPosition(ndc, map, [mapCenter.lng, mapCenter.lat])
      if (!worldPos) return null

      // Convert meters → feet (scene convention)
      const clickX = worldPos.x * METERS_TO_FEET
      const clickZ = worldPos.z * METERS_TO_FEET

      // In edit mode: filter to editing building only; in view mode: test all obstacles
      let buildingObstacles: Obstacle[]
      if (editingBuildingId) {
        const editingBuilding = buildings.find(b => b.id === editingBuildingId)
        if (!editingBuilding) return null
        const editingUnitIds = new Set(
          editingBuilding.generatedUnits?.map(u => u.id) ?? []
        )
        buildingObstacles = obstacles.filter(o => editingUnitIds.has(o.unitId))
      } else {
        buildingObstacles = obstacles
      }

      let bestId: string | null = null
      let bestDist = Infinity

      for (const obstacle of buildingObstacles) {
        const scenePos = getObstacleScenePosition(obstacle, buildings)
        if (!scenePos) continue

        const dx = clickX - scenePos.x
        const dz = clickZ - scenePos.z

        // Find the building this obstacle belongs to (for rotation)
        const obstacleBuildingRotation = (() => {
          for (const b of buildings) {
            if (b.generatedUnits?.some(u => u.id === obstacle.unitId)) {
              return b.rotation ?? 0
            }
          }
          return 0
        })()

        if (obstacle.shape === 'circle') {
          const r = obstacle.radius ?? 2
          const dist = Math.sqrt(dx * dx + dz * dz)
          if (dist <= r && dist < bestDist) {
            bestDist = dist
            bestId = obstacle.id
          }
        } else {
          // Rectangle: rotate click delta into obstacle-local frame
          const rotRad = -((obstacle.rotation ?? 0) + obstacleBuildingRotation) * Math.PI / 180
          const cos = Math.cos(rotRad)
          const sin = Math.sin(rotRad)
          const localDx = dx * cos - dz * sin
          const localDz = dx * sin + dz * cos
          const halfW = (obstacle.width ?? 4) / 2
          const halfD = (obstacle.depth ?? 4) / 2

          if (Math.abs(localDx) <= halfW && Math.abs(localDz) <= halfD) {
            const dist = Math.sqrt(dx * dx + dz * dz)
            if (dist < bestDist) {
              bestDist = dist
              bestId = obstacle.id
            }
          }
        }
      }

      return bestId
    },
    [map, sceneObjects, mapCenter, editingBuildingId, buildings, obstacles]
  )

  // Get world position from screen coordinates
  // Returns { sceneX, sceneZ } in scene feet (no Z negation)
  // Uses fixed mapCenter (scene origin) — NOT map.getCenter() (camera position)
  const getWorldPosition = useCallback(
    (screenX: number, screenY: number): { sceneX: number; sceneZ: number } | null => {
      if (!map || !sceneObjects?.scene || !mapCenter) return null

      const canvas = map.getCanvas()
      const rect = canvas.getBoundingClientRect()
      const ndc = new THREE.Vector2(
        ((screenX - rect.left) / rect.width) * 2 - 1,
        -((screenY - rect.top) / rect.height) * 2 + 1
      )

      const worldPos = screenToWorldPosition(ndc, map, [mapCenter.lng, mapCenter.lat])
      if (!worldPos) return null

      // Convert meters → feet, keep scene convention (+X east, +Z south)
      return { sceneX: worldPos.x * METERS_TO_FEET, sceneZ: worldPos.z * METERS_TO_FEET }
    },
    [map, sceneObjects, mapCenter]
  )

  // Handle click-to-place from preset OR creating mode
  const handlePlaceFromPreset = useCallback(
    (sceneXFeet: number, sceneZFeet: number) => {
      // Determine the data source: preset (edit-mode) or creatingDefaults (view-mode)
      const source = placingPreset ?? creatingDefaults
      if (!source || !locationId) return

      // Resolve target building: editingBuildingId (edit mode) → creatingForBuildingId → auto-detect
      const targetBuildingId = editingBuildingId ?? creatingForBuildingId
      let building = targetBuildingId ? buildings.find(b => b.id === targetBuildingId) : null

      // Auto-detect building from click position if no target specified
      if (!building) {
        const detection = detectHangarForPosition(
          [sceneXFeet, 0, sceneZFeet],
          buildings
        )
        if (detection.areaId) {
          building = buildings.find(b => b.id === detection.areaId)
        }
      }

      if (!building?.generatedUnits?.length) {
        console.warn('Obstacle placement: no building with units found at click position')
        if (isCreatingMode) cancelCreating()
        else cancelPlacing()
        return
      }

      // Detect which unit the click falls in
      const detection = detectHangarForPosition(
        [sceneXFeet, 0, sceneZFeet],
        [building]
      )

      // Use detected unit, fallback to first unit of building
      const targetUnitId = detection.unitId || building.generatedUnits[0].id
      if (!targetUnitId) {
        console.warn('Obstacle placement: no unit resolved')
        if (isCreatingMode) cancelCreating()
        else cancelPlacing()
        return
      }

      // Get unit offset for coordinate conversion
      const unit = building.generatedUnits.find(u => u.id === targetUnitId)
      const unitOffsetX = unit?.offset_x ?? 0
      const unitOffsetY = unit?.offset_y ?? 0

      // Convert scene-absolute feet → unit-local feet
      // 1. Subtract building world position
      const buildingPosX = building.position[0]
      const buildingPosZ = building.position[2]
      const buildingRotDeg = building.rotation ?? 0

      let relX = sceneXFeet - buildingPosX
      let relZ = sceneZFeet - buildingPosZ

      // 2. Inverse-rotate (undo building rotation)
      if (buildingRotDeg !== 0) {
        const rad = (buildingRotDeg * Math.PI) / 180
        const cos = Math.cos(rad)
        const sin = Math.sin(rad)
        const rx = relX * cos - relZ * sin
        const rz = relX * sin + relZ * cos
        relX = rx
        relZ = rz
      }

      // 3. Apply same centering as useObstacleRendering
      const centerX = building.width / 2
      const tpl = building.templateType as string | undefined
      const centerZ = (tpl === 'double_row' || tpl === 'nested_t_hangar') ? building.depth / 2 : 0
      const adjOffsetX = unitOffsetX - centerX
      const adjOffsetY = unitOffsetY - centerZ

      // 4. Convert to unit-local: subtract centered unit offset, negate Z for DB convention
      const positionX = relX - adjOffsetX
      const positionY = -relZ - adjOffsetY

      console.log('Obstacle placement:', { targetUnitId, positionX, positionY, sceneXFeet, sceneZFeet, buildingPosX, buildingPosZ, buildingRotDeg })

      const input: CreateObstacleInput = {
        unitId: targetUnitId,
        locationId,
        name: source.name,
        obstacleType: source.obstacleType,
        mobility: source.mobility,
        shape: source.shape,
        positionX,
        positionY,
        width: source.width,
        depth: source.depth,
        radius: source.radius,
        height: source.height,
        elevation: source.elevation,
        color: source.color,
        opacity: source.opacity,
        participatesInCollision: source.participatesInCollision,
        clearanceBufferFt: source.clearanceBufferFt,
      }

      create.mutate(input, {
        onSuccess: (newObstacle) => {
          // In create mode: transition to edit mode for the new obstacle
          if (isCreatingMode) {
            cancelCreating()
            if (newObstacle?.id) {
              selectObstacle(newObstacle.id)
            }
          }
        },
      })

      // Exit placing/creating mode after placement
      if (isCreatingMode) {
        cancelCreating()
      } else {
        cancelPlacing()
      }
    },
    [placingPreset, creatingDefaults, locationId, buildings, editingBuildingId, creatingForBuildingId, isCreatingMode, cancelPlacing, cancelCreating, create, selectObstacle]
  )

  // Check if screen position is over the rotation ring of the selected obstacle
  const isOverObstacleRotationRing = useCallback(
    (screenX: number, screenY: number): boolean => {
      const obsId = useObstacleStore.getState().selectedObstacleId
      if (!obsId) return false
      const obstacle = obstacles.find(o => o.id === obsId)
      if (!obstacle) return false
      const scenePos = getObstacleScenePosition(obstacle, buildings)
      if (!scenePos) return false
      const world = getWorldPosition(screenX, screenY)
      if (!world) return false

      // Both in meters for distance check
      const centerXm = scenePos.x * FEET_TO_METERS
      const centerZm = scenePos.z * FEET_TO_METERS
      const mouseXm = world.sceneX * FEET_TO_METERS
      const mouseZm = world.sceneZ * FEET_TO_METERS
      const dist = Math.sqrt((mouseXm - centerXm) ** 2 + (mouseZm - centerZm) ** 2)

      // Ring radius formula (matches BuildingEditorMap.tsx ring creation)
      const sizeFt = Math.max(obstacle.width ?? 0, obstacle.depth ?? 0, (obstacle.radius ?? 0) * 2, 4)
      const ringRadius = (sizeFt / 2 + 1) * FEET_TO_METERS
      const TOLERANCE = 0.15 // meters
      return dist >= ringRadius - TOLERANCE && dist <= ringRadius + TOLERANCE * 2
    },
    [obstacles, buildings, getWorldPosition]
  )

  // Mouse handlers — use Mapbox map events for consistent event pipeline
  useEffect(() => {
    if (!map || !enabled) return

    const canvas = map.getCanvas()
    const isPlacementActive = isPlacingMode || isCreatingMode

    const onMouseDown = (e: mapboxgl.MapMouseEvent) => {
      const { clientX, clientY } = e.originalEvent
      dragStartScreen.current = [clientX, clientY]
      isDragging.current = false

      if (isPlacementActive) {
        return // handled in mouseUp
      }

      // Check rotation ring first (takes priority over obstacle body)
      if (isOverObstacleRotationRing(clientX, clientY)) {
        const obsId = useObstacleStore.getState().selectedObstacleId!
        ringObstacleId.current = obsId
        isRotatingRing.current = true
        e.preventDefault()
        map.dragPan.disable()
        // Capture start angle (atan2 from center)
        const obs = obstacles.find(o => o.id === obsId)
        const scenePos = obs ? getObstacleScenePosition(obs, buildings) : null
        const world = getWorldPosition(clientX, clientY)
        if (world && scenePos) {
          const cx = scenePos.x * FEET_TO_METERS, cz = scenePos.z * FEET_TO_METERS
          const mx = world.sceneX * FEET_TO_METERS, mz = world.sceneZ * FEET_TO_METERS
          ringDragStartAngle.current = Math.atan2(mx - cx, mz - cz) * 180 / Math.PI
          ringDragStartRotation.current = obs?.rotation ?? 0
        }
        canvas.style.cursor = 'grabbing'
        return
      }

      const hitId = hitTestObstacles(clientX, clientY)
      if (hitId) {
        dragObstacleId.current = hitId
        const world = getWorldPosition(clientX, clientY)
        if (world) dragStartWorld.current = [world.sceneX, world.sceneZ]
        // Save obstacle's original position for total-delta drag
        const obs = obstacles.find(o => o.id === hitId)
        if (obs) dragStartObstaclePos.current = [obs.positionX, obs.positionY]
        // Save building rotation for inverse-rotating drag delta into building-local space
        const buildingRot = buildings.find(b =>
          b.generatedUnits?.some(u => u.id === obs?.unitId)
        )?.rotation ?? 0
        dragBuildingRotDeg.current = buildingRot
        e.preventDefault() // prevent map pan when starting obstacle drag
      } else {
        dragObstacleId.current = null
      }
    }

    const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const { clientX, clientY } = e.originalEvent

      // 1. Placement mode → crosshair + preview
      if (isPlacementActive) {
        canvas.style.cursor = 'crosshair'
        const world = getWorldPosition(clientX, clientY)
        if (world) setPreviewPosition({ x: world.sceneX, z: world.sceneZ })
        return
      }

      // 2. Ring rotation active → update angle
      if (isRotatingRing.current && ringObstacleId.current) {
        const world = getWorldPosition(clientX, clientY)
        const obs = obstacles.find(o => o.id === ringObstacleId.current)
        const scenePos = obs ? getObstacleScenePosition(obs, buildings) : null
        if (world && scenePos) {
          const cx = scenePos.x * FEET_TO_METERS, cz = scenePos.z * FEET_TO_METERS
          const mx = world.sceneX * FEET_TO_METERS, mz = world.sceneZ * FEET_TO_METERS
          const currentAngle = Math.atan2(mx - cx, mz - cz) * 180 / Math.PI
          const delta = currentAngle - ringDragStartAngle.current
          const newRotation = ((ringDragStartRotation.current + delta) % 360 + 360) % 360
          pushToCache(ringObstacleId.current, { rotation: newRotation })
        }
        canvas.style.cursor = 'grabbing'
        return
      }

      // 3. Not dragging — hover detection
      if (!dragObstacleId.current || !isDragging.current) {
        // Check rotation ring hover
        if (isOverObstacleRotationRing(clientX, clientY)) {
          canvas.style.cursor = 'grab'
          return
        }
        // Check obstacle body hover
        const hoverId = hitTestObstacles(clientX, clientY)
        if (hoverId) {
          canvas.style.cursor = 'pointer'
          useObstacleStore.getState().setHovered(hoverId)
          return
        }
        // Over nothing — clear hover (don't set cursor, let building hook handle it)
        useObstacleStore.getState().setHovered(null)

        // If no drag pending, stop here
        if (!dragObstacleId.current) return
      }

      // 4. Drag active
      const dx = clientX - dragStartScreen.current[0]
      const dy = clientY - dragStartScreen.current[1]
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist < DRAG_THRESHOLD_PIXELS) return

      if (!isDragging.current) {
        isDragging.current = true
        setDragging(true)
        map.dragPan.disable()
      }

      canvas.style.cursor = 'move'

      // Update obstacle position using total delta from drag start (avoids stale closure)
      const world = getWorldPosition(clientX, clientY)
      if (!world) return

      // Total delta from original drag start — NOT incremental per frame
      let totalDeltaX = world.sceneX - dragStartWorld.current[0]
      let totalDeltaZ = world.sceneZ - dragStartWorld.current[1]

      // Inverse-rotate scene-space delta into building-local space
      // Without this, rotated buildings map drag directions incorrectly
      if (dragBuildingRotDeg.current !== 0) {
        const rad = (dragBuildingRotDeg.current * Math.PI) / 180
        const cos = Math.cos(rad)
        const sin = Math.sin(rad)
        const rx = totalDeltaX * cos - totalDeltaZ * sin
        const rz = totalDeltaX * sin + totalDeltaZ * cos
        totalDeltaX = rx
        totalDeltaZ = rz
      }

      const newX = dragStartObstaclePos.current[0] + totalDeltaX
      const newY = dragStartObstaclePos.current[1] - totalDeltaZ

      pushToCache(dragObstacleId.current!, {
        positionX: newX,
        positionY: newY,
      })
      // DO NOT update dragStartWorld here — keep original reference point
    }

    const onMouseUp = (e: mapboxgl.MapMouseEvent) => {
      const { clientX, clientY } = e.originalEvent

      if (isPlacementActive) {
        // Only place on genuine click — ignore map pans
        const dx = clientX - dragStartScreen.current[0]
        const dy = clientY - dragStartScreen.current[1]
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist > DRAG_THRESHOLD_PIXELS) return

        const world = getWorldPosition(clientX, clientY)
        if (world) {
          handlePlaceFromPreset(world.sceneX, world.sceneZ)
        }
        return
      }

      // End ring rotation
      if (isRotatingRing.current) {
        isRotatingRing.current = false
        ringObstacleId.current = null
        map.dragPan.enable()
        canvas.style.cursor = 'default'
        onObstacleUpdate?.()
        return
      }

      if (isDragging.current) {
        isDragging.current = false
        setDragging(false)
        map.dragPan.enable()
        canvas.style.cursor = 'default'
        onObstacleUpdate?.()
      } else {
        // Click → select (only if clicked on an obstacle, NOT background)
        const hitId = hitTestObstacles(clientX, clientY)
        if (hitId) {
          selectObstacle(hitId)
        }
      }

      dragObstacleId.current = null
    }

    map.on('mousedown', onMouseDown)
    map.on('mousemove', onMouseMove)
    map.on('mouseup', onMouseUp)

    return () => {
      map.off('mousedown', onMouseDown)
      map.off('mousemove', onMouseMove)
      map.off('mouseup', onMouseUp)
    }
  }, [
    map, enabled, isPlacingMode, isCreatingMode, obstacles, buildings,
    hitTestObstacles, getWorldPosition, isOverObstacleRotationRing, handlePlaceFromPreset,
    selectObstacle, setDragging, setPreviewPosition, pushToCache, onObstacleUpdate,
  ])

  // Keyboard shortcuts
  useEffect(() => {
    if (!enabled || !selectedObstacleId) return

    const obstacle = obstacles.find((o) => o.id === selectedObstacleId)
    if (!obstacle) return

    const onKeyDown = (e: KeyboardEvent) => {
      // Delete → soft-delete (not Backspace — conflicts with form input)
      if (e.key === 'Delete') {
        e.preventDefault()
        remove.mutate(obstacle.id)
        selectObstacle(null)
        return
      }

      // R → rotate 90°, Shift+R → rotate 15°
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault()
        const step = e.shiftKey ? 15 : 90
        const newRotation = (obstacle.rotation + step) % 360
        update.mutate({ id: obstacle.id, data: { rotation: newRotation } })
        return
      }

      // Ctrl+D → duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault()
        duplicate.mutate(obstacle)
        return
      }

      // Escape → cancel placing or deselect
      if (e.key === 'Escape') {
        if (isPlacingMode) {
          cancelPlacing()
        } else {
          selectObstacle(null)
        }
        return
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    enabled, selectedObstacleId, obstacles, isPlacingMode,
    remove, update, duplicate, selectObstacle, cancelPlacing,
  ])

  // Escape to cancel placing/creating mode (when no obstacle selected)
  useEffect(() => {
    if (!enabled || (!isPlacingMode && !isCreatingMode)) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isCreatingMode) cancelCreating()
        if (isPlacingMode) cancelPlacing()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [enabled, isPlacingMode, isCreatingMode, cancelPlacing, cancelCreating])
}
